import React from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { useUnit } from 'effector-react';
import { $user } from 'models/user';
import CandidatesPage from '@/pages/Candidates/CandidatesPage';
import Layout from '@/pages/Layout';
import Login from '@/components/Login';
import NotFound from '@/components/NotFound/NotFound';
import AccessDenied from '@/components/AccessDenied/AccessDenied';
import { CachePolicies, Provider } from 'use-http';
import toast from 'react-hot-toast';
import DashboardPage from '@/pages/Dashboard/DashboardPage';
import { GroupsEnum } from '@/types/users';

const host = process.env.REACT_APP_BACKEND_URL;

const normalizeGroup = (group: string) => group.trim().toLocaleLowerCase('ru-RU');

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const user = useUnit($user);
  const { keycloak } = useKeycloak();
  if (!keycloak.authenticated || !user) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
};

const RecruiterRoute = ({ children }: { children: React.ReactNode }) => {
  const user = useUnit($user);
  const hasRecruiterGroup = user?.groups?.some(
    (group) => normalizeGroup(group) === normalizeGroup(GroupsEnum.RECRUTER),
  );

  if (!hasRecruiterGroup) {
    return <AccessDenied />;
  }
  return <>{children}</>;
};

const AppRouter = () => {
  const { keycloak, initialized } = useKeycloak();
  const navigate = useNavigate();

  if (!initialized) {
    return <div>Загрузка...</div>;
  }

  const globalOptions = {
    cachePolicy: CachePolicies.NO_CACHE,
    interceptors: {
      request: ({ options }: { options: RequestInit }) => {
        const newOptions = options;
        newOptions.headers = {
          Authorization: `Bearer ${keycloak.token}`,
          ...options.headers,
        };
        return newOptions;
      },
      response: async (args: { response: Response; request: RequestInit }) => {
        const { response } = args;
        if (!response.ok) {
          let errorMessage = 'Произошла ошибка при запросе. Попробуйте позже.';

          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
          } catch (e) {
            console.error('Не удалось распарсить ответ:', e);
          }

          if (response.status >= 400 && response.status < 500) {
            errorMessage = errorMessage || 'Ошибка клиента. Проверьте запрос или ресурс.';
          } else if (response.status >= 500) {
            errorMessage =
              'Возникли технические проблемы. Попробуйте обновить страницу или вернуться чуть позже. Если проблема сохраняется, обратитесь в Сервис Деск компании.';
          }

          toast.error(errorMessage);

          navigate('/');

          throw new Error(errorMessage);
        }

        return response;
      },
    },
  };
  return (
    <Provider url={host} options={globalOptions}>
      <Routes>
        <Route
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route
            path="candidates"
            element={
              <RecruiterRoute>
                <CandidatesPage />
              </RecruiterRoute>
            }
          />
        </Route>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Provider>
  );
};

export default AppRouter;
